import { Component, ElementRef, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Subject, take, takeUntil } from 'rxjs';

import { AuthService } from '../../../auth.service';
import { AppService } from '../../../app.service';
import { LoaderService } from '../../../loader.service';

import { HeaderComponent } from '../../../header/header.component';
import { ProgressBarComponent } from '../../../shared/progress-bar/progress-bar.component';
import { ErrorDialogComponent } from '../../../dialog/error-dialog/error-dialog.component';
import { GoBackButtonComponent } from '../../../shared/go-back-button/go-back-button.component';
import { LoaderComponent } from '../../../shared/loader/loader.component';

import { emailOrPhoneValidator } from '../../../shared/validators/emailOrPhoneValidator';
import { firstnameValidator } from '../../../shared/validators/firstnameValidator';

import { ClickAnywhereDirective } from '../../../click-anywhere.directive';
import { ErrorReturn } from '../../../shared/interfaces/errorReturn';

@Component({
  selector: 'app-auth-subscribe-third',
  standalone: true,
  imports: [
    FormsModule, 
    ReactiveFormsModule, 
    HeaderComponent, 
    CommonModule, 
    ProgressBarComponent, 
    ErrorDialogComponent, 
    GoBackButtonComponent, 
    RouterModule, 
    LoaderComponent, 
    ClickAnywhereDirective
  ],
  templateUrl: './auth-subscribe-third.component.html',
  styleUrl: './auth-subscribe-third.component.scss'
})
export class AuthSubscribeThirdComponent implements OnInit, OnDestroy {
  @ViewChild('errorMessageDialog') errorMessageDialog!: ElementRef

  loading$ = this.loaderService.loading$
  subThirdFormGroup!: FormGroup
  error = false
  showPassword = false
  errorMessage: string | null = null
  errorTitle: string | null = null
  openDialog = false
  userBilled = false
  
  hightlightedSteps: {
    first: boolean, 
    second?: boolean, 
    third?: boolean,
    fourth?: boolean
  } = { first: true, second: true, third: true }
  passwordMinCharNumber = 6
  passwordMaxCharNumber = 25
  firstnameMinCharNumber = 2
  firstnameMaxCharNumber = 25
  private readonly _destroyed = new Subject<void>()

  constructor(
    private authService: AuthService,
    private appService: AppService,
    private router: Router,
    private loaderService: LoaderService
  ){
    this.subThirdFormGroup = new FormGroup({
      firstnameCtrl: new FormControl(this.authService.get('firstname') || '', [Validators.required, Validators.minLength(this.firstnameMinCharNumber), Validators.maxLength(this.firstnameMaxCharNumber), firstnameValidator()]),
      emailCtrl: new FormControl(this.authService.get('email') || '', [Validators.required, Validators.minLength(2), Validators.maxLength(90), emailOrPhoneValidator()]),
      passwordCtrl: new FormControl('', [Validators.required, Validators.minLength(this.passwordMinCharNumber), Validators.maxLength(this.passwordMaxCharNumber)]),
    });
  }

  ngOnInit() {
    // check if information are available from subscription steps 1 and 2
    if(!this.authService.get('sex') || !this.authService.get('birthdate')) this.router.navigate(['/subscribe-step-one'])
    if(!this.authService.get('city_id') || !this.authService.get('city_name')) this.router.navigate(['/subscribe-step-two'])

    this.userBilled = this.authService.userBilled;

    // get auto-generated data from the back to pre-fill the form
    this.authService.getGenericUserData().pipe(take(1)).subscribe(data => {
      this.subThirdFormGroup.setValue({
        firstnameCtrl: data.name,
        emailCtrl: data.email,
        passwordCtrl: data.password
      })
    })
  }

  getClickNature(clickNature: string){
    if(clickNature === 'anywhere' && this.openDialog === true){
      this.openDialog = false
    }
  }

  onSubmit(data: {firstnameCtrl: string, emailCtrl: string, passwordCtrl: string}){
    this.loaderService.show()
    this.authService.setSubscriptionFormData('firstname', data.firstnameCtrl)
    this.authService.setSubscriptionFormData('email', data.emailCtrl.toLowerCase())
    this.authService.setSubscriptionFormData('password', data.passwordCtrl)
    this.authService.set('firstname', data.firstnameCtrl)
    this.authService.set('email', data.emailCtrl)

    const userData = {
      attraction: this.authService.subscriptionFormData.get('sex') === 'man' ? 'woman' : 'man',
      birthdate: this.authService.subscriptionFormData.get('birthdate') as string,
      city_id: parseInt(this.authService.subscriptionFormData.get('city_id') as string),
      firstname: this.authService.subscriptionFormData.get('firstname') as string,
      email: this.authService.subscriptionFormData.get('email') as string,
      password: this.authService.subscriptionFormData.get('password') as string,
      sex: this.authService.subscriptionFormData.get('sex') as string
    }

    // the user exists on kliento but not on the database
    if(this.userBilled){
      const billedData = { ...userData, phoneNumber: this.authService.get('msisdn') };

      this.authService.authSubscribePostBilling(billedData).pipe(takeUntil(this._destroyed)).subscribe({
        next: () => {
          this.subscriptionStep2();
        },
        error: () => {
          this.authService.remove('token')
          this.loaderService.hide()
        },
      });
    } else {
      this.authService.authSubscribe().pipe(takeUntil(this._destroyed)).subscribe({
        next: (res => {
          if(res.datas?.user && res.datas?.api_token){
            this.authService.set('token', res.datas.api_token)
            this.authService.set('user_id', res.datas?.user.user_id.toString())

            this.authService.getProfile(res.datas?.user.user_id).pipe(takeUntil(this._destroyed)).subscribe({
              next: (res => {
                this.authService.set('user', JSON.stringify(res))
                this.loaderService.hide()

                this.router.navigate(['/subscribe-step-four'])
              }),
              error: (error => {
                console.error(error)
                /* this.errorTitle = 'Login failed'
                this.errorMessage = error?.error.message
                this.openDialog = true */
              })
            }) 
          }
        }),
        error: (error => {
          if(error.status === 502){
            const err: ErrorReturn = {
              code: 502,
              message: error.error.message
            }
            this.appService.setPopin(err)
          } else {
            this.appService.setPopin(error.error)
          }
          
          this.loaderService.hide()
        })
      })
    }
  }

  subscriptionStep2(){
    this.authService.authLogin(this.authService.subscriptionFormData.get('email') as string, this.authService.subscriptionFormData.get('password') as string)
      .subscribe({
        next: () => {
          this.authService.logged$.next(true);
          this.loaderService.hide()

          this.router.navigate(['/subscribe-step-four'])
        },
        error: () => {
          console.log('error');
        },
      });
  }

  setFocusInput(){
    this.openDialog = false
  }
  
  setBlurInput() {}

  showHidePassword(){
    this.showPassword = !this.showPassword;
  }

  closeDialog(){
    this.openDialog = false
  }

  ngOnDestroy(){
    this._destroyed.next()
    this._destroyed.complete()
  }
}
